import React, { useState, useEffect, useCallback } from "react";
import {
  Paintbrush,
  Copy,
  Eye,
  Trash2,
  Plus,
  Play,
  Code,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  AlertCircle,
  Check,
  X,
} from "lucide-react";
import api from "../api/client";

export default function PageDesigner({ selectedSite }) {
  const siteId = selectedSite?.id;
  const [tab, setTab] = useState("clone"); // clone | css
  const [pages, setPages] = useState([]);
  const [loadingPages, setLoadingPages] = useState(false);

  // Clone state
  const [sourceUrl, setSourceUrl] = useState("");
  const [targetPageId, setTargetPageId] = useState("");
  const [keepContent, setKeepContent] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [cloning, setCLoning] = useState(false);
  const [extraction, setExtraction] = useState(null);
  const [cloneResult, setCloneResult] = useState(null);
  const [cloneError, setCloneError] = useState("");

  // CSS Injections state
  const [injections, setInjections] = useState([]);
  const [loadingInjections, setLoadingInjections] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [showNewRule, setShowNewRule] = useState(false);

  // Fetch pages list
  const fetchPages = useCallback(async () => {
    if (!siteId) return;
    setLoadingPages(true);
    try {
      const res = await api.get(`/websites/${siteId}/page-designer/pages/`);
      setPages(Array.isArray(res.data) ? res.data : []);
    } catch {
      setPages([]);
    }
    setLoadingPages(false);
  }, [siteId]);

  // Fetch CSS injections
  const fetchInjections = useCallback(async () => {
    if (!siteId) return;
    setLoadingInjections(true);
    try {
      const res = await api.get(
        `/websites/${siteId}/page-designer/css-injections/`
      );
      setInjections(Array.isArray(res.data) ? res.data : []);
    } catch {
      setInjections([]);
    }
    setLoadingInjections(false);
  }, [siteId]);

  useEffect(() => {
    fetchPages();
    fetchInjections();
  }, [fetchPages, fetchInjections]);

  // ─── Extract Design ───
  const handleExtract = async () => {
    if (!sourceUrl) return;
    setExtracting(true);
    setExtraction(null);
    setCloneError("");
    try {
      const res = await api.post(
        `/websites/${siteId}/page-designer/extract/`,
        { source_url: sourceUrl }
      );
      setExtraction(res.data);
    } catch (err) {
      setCloneError(
        err.response?.data?.error || "Failed to extract design"
      );
    }
    setExtracting(false);
  };

  // ─── Quick Clone ───
  const handleQuickClone = async (dryRun = true) => {
    if (!sourceUrl || !targetPageId) return;
    setCLoning(true);
    setCloneResult(null);
    setCloneError("");
    try {
      const res = await api.post(
        `/websites/${siteId}/page-designer/quick-clone/`,
        {
          source_url: sourceUrl,
          target_page_id: parseInt(targetPageId),
          keep_target_content: keepContent,
          dry_run: dryRun,
        }
      );
      setCloneResult(res.data);
    } catch (err) {
      setCloneError(
        err.response?.data?.error || "Clone operation failed"
      );
    }
    setCLoning(false);
  };

  // ─── CSS Injection CRUD ───
  const handleSaveRule = async (rule) => {
    try {
      await api.post(
        `/websites/${siteId}/page-designer/css-injections/upsert/`,
        rule
      );
      fetchInjections();
      setEditingRule(null);
      setShowNewRule(false);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to save rule");
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm("Delete this CSS injection rule?")) return;
    try {
      await api.delete(
        `/websites/${siteId}/page-designer/css-injections/${ruleId}/`
      );
      fetchInjections();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete rule");
    }
  };

  if (!siteId) {
    return (
      <div className="p-6 text-gray-400">Select a site to use Page Designer.</div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Paintbrush className="text-copper" size={28} />
            Page Designer
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Clone page designs and manage CSS injections across{" "}
            {selectedSite?.domain}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-dark-300 p-1 rounded-lg w-fit">
        {[
          { id: "clone", label: "Design Cloner", icon: Copy },
          { id: "css", label: "CSS Injections", icon: Code },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === id
                ? "bg-copper text-white"
                : "text-gray-400 hover:text-white"
            }`}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {/* ─── CLONE TAB ─── */}
      {tab === "clone" && (
        <div className="space-y-6">
          {/* Source + Target Selection */}
          <div className="bg-dark-300 rounded-xl border border-dark-50 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white">
              Clone Page Design
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Source URL */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Source Page URL
                </label>
                <input
                  type="url"
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  placeholder="https://example.com/page-to-clone/"
                  className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-white text-sm focus:border-copper focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  The page whose design you want to copy
                </p>
              </div>

              {/* Target Page */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Target Page
                </label>
                <select
                  value={targetPageId}
                  onChange={(e) => setTargetPageId(e.target.value)}
                  className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-white text-sm focus:border-copper focus:outline-none"
                >
                  <option value="">Select target page...</option>
                  {pages.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title} (/{p.slug}/)
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  The page to apply the design to
                </p>
              </div>
            </div>

            {/* Options */}
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={keepContent}
                  onChange={(e) => setKeepContent(e.target.checked)}
                  className="rounded border-dark-50 bg-dark text-copper focus:ring-copper"
                />
                Keep target page's content (swap into source layout)
              </label>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleExtract}
                disabled={!sourceUrl || extracting}
                className="flex items-center gap-2 px-4 py-2 bg-dark-50 hover:bg-dark text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
              >
                <Eye size={16} />
                {extracting ? "Extracting..." : "Extract Design"}
              </button>
              <button
                onClick={() => handleQuickClone(true)}
                disabled={!sourceUrl || !targetPageId || cloning}
                className="flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
              >
                <Play size={16} />
                {cloning ? "Cloning..." : "Preview Clone (Dry Run)"}
              </button>
            </div>
          </div>

          {/* Error */}
          {cloneError && (
            <div className="bg-red-900/20 border border-red-800/30 rounded-xl p-4 flex items-center gap-3 text-red-400 text-sm">
              <AlertCircle size={18} />
              {cloneError}
            </div>
          )}

          {/* Extraction Results */}
          {extraction && (
            <div className="bg-dark-300 rounded-xl border border-dark-50 p-6 space-y-4">
              <h3 className="text-lg font-semibold text-white">
                Extracted Design
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard
                  label="CSS"
                  value={`${(extraction.css?.length || 0).toLocaleString()} chars`}
                />
                <StatCard
                  label="Sections"
                  value={extraction.sections?.length || 0}
                />
                <StatCard
                  label="Images"
                  value={extraction.images?.length || 0}
                />
                <StatCard
                  label="Google Fonts"
                  value={extraction.google_fonts?.length || 0}
                />
              </div>

              {/* Sections */}
              {extraction.sections?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-2">
                    Sections Found
                  </h4>
                  <div className="space-y-1">
                    {extraction.sections.map((s, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 px-3 py-2 bg-dark rounded-lg text-sm"
                      >
                        <span className="px-2 py-0.5 bg-copper/20 text-copper rounded text-xs font-mono">
                          {s.type}
                        </span>
                        <span className="text-white">
                          {s.content?.heading || s.id}
                        </span>
                        {s.content?.items && (
                          <span className="text-gray-500 text-xs">
                            ({s.content.items.length} items)
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Clone Result */}
          {cloneResult && (
            <div className="bg-dark-300 rounded-xl border border-dark-50 p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">
                  {cloneResult.dry_run
                    ? "Clone Preview (Dry Run)"
                    : "Clone Applied"}
                </h3>
                {cloneResult.dry_run && (
                  <button
                    onClick={() => handleQuickClone(false)}
                    disabled={cloning}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                  >
                    <Check size={16} />
                    Apply Changes
                  </button>
                )}
                {cloneResult.pushed && (
                  <div className="flex items-center gap-2 text-green-400 text-sm">
                    <Check size={16} />
                    Changes pushed successfully
                    {cloneResult.page_url && (
                      <a
                        href={cloneResult.page_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-copper hover:underline flex items-center gap-1"
                      >
                        View <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard
                  label="Target"
                  value={cloneResult.target_title || `Page ${cloneResult.target_page_id}`}
                />
                <StatCard
                  label="CSS"
                  value={`${(cloneResult.css?.length || 0).toLocaleString()} chars`}
                />
                <StatCard
                  label="Before"
                  value={`${(cloneResult.before_html?.length || 0).toLocaleString()} chars`}
                />
                <StatCard
                  label="After"
                  value={`${(cloneResult.after_html?.length || 0).toLocaleString()} chars`}
                />
              </div>

              {/* Before/After Preview */}
              <PreviewToggle
                label="Before HTML"
                content={cloneResult.before_html}
              />
              <PreviewToggle
                label="After HTML"
                content={cloneResult.after_html}
              />
              {cloneResult.css && (
                <PreviewToggle label="CSS" content={cloneResult.css} />
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── CSS INJECTIONS TAB ─── */}
      {tab === "css" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">
              CSS Injection Rules
            </h2>
            <div className="flex gap-2">
              <button
                onClick={fetchInjections}
                className="flex items-center gap-2 px-3 py-2 bg-dark-50 hover:bg-dark text-gray-300 rounded-lg text-sm"
              >
                <RefreshCw size={14} />
                Refresh
              </button>
              <button
                onClick={() => {
                  setShowNewRule(true);
                  setEditingRule({
                    name: "",
                    css: "",
                    target_type: "all",
                    target_value: "",
                    enabled: true,
                    priority: 10,
                  });
                }}
                className="flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white rounded-lg text-sm font-medium"
              >
                <Plus size={16} />
                New Rule
              </button>
            </div>
          </div>

          {loadingInjections && (
            <div className="text-gray-400 text-sm animate-pulse">
              Loading...
            </div>
          )}

          {/* New Rule Form */}
          {showNewRule && editingRule && (
            <CSSRuleForm
              rule={editingRule}
              onSave={handleSaveRule}
              onCancel={() => {
                setShowNewRule(false);
                setEditingRule(null);
              }}
            />
          )}

          {/* Rules List */}
          {injections.length === 0 && !loadingInjections && (
            <div className="bg-dark-300 rounded-xl border border-dark-50 p-8 text-center text-gray-500 text-sm">
              No CSS injection rules yet. Click "New Rule" to create one.
            </div>
          )}

          {injections.map((rule) => (
            <div
              key={rule.id}
              className="bg-dark-300 rounded-xl border border-dark-50 p-4"
            >
              {editingRule?.id === rule.id ? (
                <CSSRuleForm
                  rule={editingRule}
                  onSave={handleSaveRule}
                  onCancel={() => setEditingRule(null)}
                />
              ) : (
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <span
                        className={`w-2 h-2 rounded-full ${
                          rule.enabled ? "bg-green-400" : "bg-gray-600"
                        }`}
                      />
                      <h3 className="text-white font-medium text-sm truncate">
                        {rule.name || rule.id}
                      </h3>
                      <span className="px-2 py-0.5 bg-dark-50 text-gray-400 rounded text-xs font-mono">
                        {rule.target_type}
                        {rule.target_value ? `: ${rule.target_value}` : ""}
                      </span>
                      <span className="text-xs text-gray-600">
                        Priority: {rule.priority}
                      </span>
                    </div>
                    <div className="mt-2 text-xs text-gray-500 font-mono truncate">
                      {rule.css?.substring(0, 120)}...
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setEditingRule({ ...rule })}
                      className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-dark-50 transition-colors"
                      title="Edit"
                    >
                      <Code size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteRule(rule.id)}
                      className="p-2 text-gray-400 hover:text-red-400 rounded-lg hover:bg-dark-50 transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ───

function StatCard({ label, value }) {
  return (
    <div className="bg-dark rounded-lg p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-white font-semibold text-sm mt-0.5 truncate">
        {value}
      </div>
    </div>
  );
}

function PreviewToggle({ label, content }) {
  const [open, setOpen] = useState(false);
  if (!content) return null;

  return (
    <div className="border border-dark-50 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2 text-sm text-gray-300 hover:bg-dark-50 transition-colors"
      >
        <span>
          {label} ({content.length.toLocaleString()} chars)
        </span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <pre className="p-4 bg-dark text-xs text-gray-400 overflow-auto max-h-96 font-mono whitespace-pre-wrap">
          {content}
        </pre>
      )}
    </div>
  );
}

function CSSRuleForm({ rule, onSave, onCancel }) {
  const [form, setForm] = useState({ ...rule });

  const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Name</label>
          <input
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Rule name"
            className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-white text-sm focus:border-copper focus:outline-none"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Target Type
            </label>
            <select
              value={form.target_type}
              onChange={(e) => update("target_type", e.target.value)}
              className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-white text-sm focus:border-copper focus:outline-none"
            >
              <option value="all">All Pages</option>
              <option value="page_id">Page ID(s)</option>
              <option value="slug_contains">Slug Contains</option>
              <option value="slug_list">Slug List</option>
              <option value="post_type">Post Type</option>
              <option value="url_regex">URL Regex</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">
              Target Value
            </label>
            <input
              value={form.target_value}
              onChange={(e) => update("target_value", e.target.value)}
              placeholder={
                form.target_type === "all" ? "(none needed)" : "value"
              }
              disabled={form.target_type === "all"}
              className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-white text-sm focus:border-copper focus:outline-none disabled:opacity-50"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-xs text-gray-500 mb-1">CSS</label>
        <textarea
          value={form.css}
          onChange={(e) => update("css", e.target.value)}
          placeholder=".my-class { color: red; }"
          rows={8}
          className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-white text-sm font-mono focus:border-copper focus:outline-none"
        />
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => update("enabled", e.target.checked)}
              className="rounded border-dark-50 bg-dark text-copper focus:ring-copper"
            />
            Enabled
          </label>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500">Priority:</label>
            <input
              type="number"
              value={form.priority}
              onChange={(e) => update("priority", parseInt(e.target.value) || 10)}
              className="w-16 bg-dark border border-dark-50 rounded px-2 py-1 text-white text-sm focus:border-copper focus:outline-none"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="flex items-center gap-1 px-3 py-2 text-gray-400 hover:text-white rounded-lg text-sm"
          >
            <X size={14} />
            Cancel
          </button>
          <button
            onClick={() => onSave(form)}
            className="flex items-center gap-1 px-4 py-2 bg-copper hover:bg-copper-600 text-white rounded-lg text-sm font-medium"
          >
            <Check size={14} />
            Save Rule
          </button>
        </div>
      </div>
    </div>
  );
}
